import React, { useEffect, useState, useMemo } from 'react';
import { View, Text, TextInput, Image } from 'react-native';
import { useTheme } from '../../theme/useTheme';
import { styles } from './Input.styles';
import { InputProps } from './InputProps';
import usd from '../../assets/usd-3x.png';
import InputError from './InputError';
import { formatAmountWithCommas } from './helpers';

const InputField: React.FC<InputProps> = ({
  variant,
  label,
  value,
  disabled,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  editable = true,
  required = false,
  maxLength = 100,
  isError = false,
  errorText = '',
  testID,
  tourId,
}) => {
  const { theme } = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentState =
    error || isError ? 'error' : isFocused ? 'focused' : value ? 'filled' : 'default';

  const getBorderColor = () => {
    if (disabled) return theme.gray_blue[3];
    if (!editable) return theme.gray_blue[6];
    switch (currentState) {
      case 'error':
        return theme.red[3];
      case 'focused':
        return theme.brand_blue[3];
      case 'filled':
        return theme.gray_blue[6];
      default:
        return theme.gray_blue[6];
    }
  };

  const getLabelColor = () => {
    if (disabled) return theme.gray_blue[3];
    return currentState === 'error' ? theme.red[3] : theme.gray_blue[1];
  };

  const getRequiredIndicatorColor = () => {
    return disabled ? theme.gray_blue[3] : theme.red[3];
  };

  const validateInput = (text: string) => {
    if (maxLength && text.length > maxLength) {
      setError(`Maximum ${maxLength} characters allowed`);
      return;
    }

    switch (variant) {
      case 'caption':
        if (!/^[a-zA-Z0-9\s,.\-&/#'"]*$/.test(text)) {
          setError(
            'Only alphanumeric characters and common address symbols (, . - & / # \' ") allowed',
          );
        } else {
          setError(null);
        }
        break;
      case 'amount': {
        // allow empty while editing (user-focused clear) or if field is not required
        if ((isFocused || !required) && text.trim() === '') {
          setError(null);
        } else if (!/^\d+(\.\d{0,2})?$/.test(text)) {
          setError('Enter a valid amount');
        } else {
          setError(null);
        }
        break;
      }
      default:
        setError(null);
    }
    onChange(text);
  };

  useEffect(() => {
    if (value !== undefined && value !== null) {
      validateInput(value.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const inputStyles = [
    styles.input,
    {
      outline: 'none' as const,
      borderColor: getBorderColor(),
      color: disabled ? theme.gray_blue[4] : theme.gray_blue[1],
    },
    variant === 'amount' && styles.amountInput,
  ];

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  // ---- amount display logic (only affects what is SHOWN, not the stored value) ----
  const amountDisplayValue = useMemo(() => {
    if (variant !== 'amount') return undefined;

    const raw = value ?? '';
    const rawStr = typeof raw === 'number' ? String(raw) : String(raw);
    const numericZero =
      rawStr.trim() === '' || rawStr === '0' || rawStr === '0.' || Number(rawStr) === 0;

    // while focused: show "" if empty/zero; otherwise show actual (unformatted) value
    if (isFocused) {
      return numericZero ? '' : rawStr;
    }

    // when not focused: show "0" if empty/zero; otherwise show formatted with commas
    if (numericZero) return '0';
    return formatAmountWithCommas(rawStr);
  }, [variant, value, isFocused]);

  const displayedValue = variant === 'amount' ? amountDisplayValue! : (value?.toString() ?? '');

  // On blur, validate the RAW number string (strip commas if we added them for display)
  useEffect(() => {
    if (!isFocused) {
      if (variant === 'amount') {
        const rawForValidation = displayedValue.replace(/,/g, '');
        validateInput(rawForValidation);
      } else {
        validateInput(displayedValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  return (
    <View>
      <View style={styles.labelContainer}>
        <Text style={[styles.label, { color: getLabelColor() }]}>{label}</Text>
        {required && (
          <Text
            style={[
              styles.requiredIndicator,
              { color: getRequiredIndicatorColor(), marginLeft: 4 },
            ]}>
            *
          </Text>
        )}
      </View>

      <View
        style={[styles.inputContainer, { borderColor: getBorderColor() }]}
        {...(tourId && { 'data-tour': tourId })}>
        {variant === 'amount' && (
          <View style={styles.iconContainer}>
            <Image source={usd} style={styles.iconImage} resizeMode="contain" />
          </View>
        )}

        <TextInput
          testID={testID}
          style={[inputStyles, styles.flexInput]}
          value={displayedValue}
          onChangeText={validateInput}
          editable={editable}
          onFocus={handleFocus}
          onBlur={handleBlur}
          keyboardType={variant === 'amount' ? 'numeric' : 'default'}
          placeholder={placeholder || label}
          placeholderTextColor={theme.gray_blue[3]}
          maxLength={maxLength}
        />
      </View>

      {error || isError ? <InputError errorText={isError ? errorText : (error as string)} /> : null}
    </View>
  );
};

export default InputField;
